/**
 * URL utilities
 */

import { buildLocalePath, extractLocaleFromPath } from "@/utils/locale-url";

/**
 * Normalizes a URL to a clean relative path without locale prefix.
 *
 * Handles:
 *  1. Absolute URLs (https://…) → extracts the pathname
 *  2. "/global/" prefix (Umbraco default culture) → strips it
 *  3. Existing locale prefix ("/nb-NO/…", "/en/…") → strips it
 *
 * Examples:
 *   "https://wehunt.no/nb-no/terms-conditions/" → "/terms-conditions/"
 *   "/global/products/tracker-luna"              → "/products/tracker-luna"
 *   "/global/"                                   → "/"
 *   "/nb-NO/some-page"                           → "/some-page"
 *   "/products/luna"                             → "/products/luna"
 */
export function sanitizeUrl(url: string): string {
  let path = url;

  // 1. Absolute URLs → extract just the pathname
  if (path.startsWith("http://") || path.startsWith("https://")) {
    try {
      path = new URL(path).pathname;
    } catch {
      // Malformed URL — use as-is
    }
  }

  // 2. Strip "/global/" prefix
  if (path.startsWith("/global/")) {
    path = path.replace("/global/", "/");
    return path || "/";
  }
  if (path === "/global") {
    return "/";
  }

  // 3. Strip existing locale prefix (e.g. "/nb-NO/…", "/en/…")
  const locale = extractLocaleFromPath(path);
  if (locale) {
    const segments = path.split("/").filter(Boolean);
    path = "/" + segments.slice(1).join("/");
    if (path !== "/" && url.endsWith("/")) {
      path += "/";
    }
    return path || "/";
  }

  return path;
}

/**
 * Sanitize a URL and prepend the current locale prefix, in its lower-case URL
 * form. Used for all internal navigation links so they carry the locale.
 *
 * Examples (locale = "sv-SE"):
 *   "/global/products/luna" → "/sv-se/products/luna"
 *   "/products/luna"        → "/sv-se/products/luna"
 *   "/"                     → "/sv-se/"
 */
export function localizeUrl(url: string, locale: string): string {
  return buildLocalePath(locale, sanitizeUrl(url));
}

/**
 * Reduces a page URL to a relative router slug: no locale prefix, no leading or
 * trailing slashes. Used to match a CMS page (e.g. My Account) by its localized,
 * editor-controlled slug.
 *
 * Examples:
 *   "/global/oma-tili/"                  → "oma-tili"
 *   "https://wehunt.app/fi-FI/oma-tili"  → "oma-tili"
 *   "/my-account"                        → "my-account"
 */
export function toRouteSlug(url: string, fallback = ""): string {
  return sanitizeUrl(url).replace(/^\/+|\/+$/g, "") || fallback;
}

const VALIDATION_BASE = "http://redirect-validation.invalid";

/**
 * Validates an untrusted navigation target and returns a normalized path
 * (pathname + search + hash), or undefined when the value is not safe to
 * navigate to. Rejects schemes ("javascript:", "data:"), protocol-relative
 * URLs ("//host"), backslashes, and CR/LF/tab injection.
 *
 * Without `allowedOrigin` only relative paths ("/…") pass. With it, an
 * absolute http(s) URL on exactly that origin also passes and is reduced to
 * its path — Umbraco returns absolute URLs for cultures bound to a domain.
 *
 * Examples:
 *   toSafeRelativePath("/fi-FI/oma-tili")                               → "/fi-FI/oma-tili"
 *   toSafeRelativePath("javascript:alert(1)")                           → undefined
 *   toSafeRelativePath("//evil.example/x")                              → undefined
 *   toSafeRelativePath("https://wehunt.no/nb-no/", "https://wehunt.no") → "/nb-no/"
 *   toSafeRelativePath("https://evil.example/", "https://wehunt.no")    → undefined
 */
export function toSafeRelativePath(
  raw: unknown,
  allowedOrigin?: string
): string | undefined {
  if (typeof raw !== "string") return undefined;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return undefined;
  if (/[\r\n\t]/.test(trimmed)) return undefined;
  if (trimmed.includes("\\")) return undefined;

  if (/^https?:\/\//i.test(trimmed)) {
    if (!allowedOrigin) return undefined;
    try {
      const url = new URL(trimmed);
      if (url.origin !== new URL(allowedOrigin).origin) return undefined;
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return undefined;
    }
  }

  if (!trimmed.startsWith("/")) return undefined;
  if (trimmed.startsWith("//")) return undefined;
  try {
    const url = new URL(trimmed, VALIDATION_BASE);
    if (url.origin !== VALIDATION_BASE) return undefined;
    const normalized = `${url.pathname}${url.search}${url.hash}`;
    if (!normalized.startsWith("/") || normalized.startsWith("//")) {
      return undefined;
    }
    return normalized;
  } catch {
    return undefined;
  }
}

/**
 * Resolves the correct href for a UmbracoLink based on its type.
 *
 * - "External" links → return url as-is (preserve full external URL)
 * - "Content" links  → sanitize + prepend current locale prefix
 * - Other types      → return url as-is
 *
 * This is the primary function components should use when rendering
 * UmbracoLink objects. It replaces ad-hoc target-based branching
 * that previously used sanitizeUrl for external links (which stripped
 * their domains).
 */
export function resolveHref(
  link: { type: string; url: string },
  locale: string
): string {
  if (link.type === "Content") {
    return localizeUrl(link.url, locale);
  }
  // External, Image, Media — keep the original URL
  return link.url;
}

/**
 * Resolves a bare URL string the backend read off a CMS link picker, where the
 * link's type did not travel with it (e.g. the My Account recommendation
 * targets, GROW-397). Rendered the way `resolveHref` renders links:
 *
 * - a site path gets the current locale prefix, as a Content link does — with
 *   its query and fragment kept, which `localizeUrl` alone would treat as path;
 * - an absolute URL on this origin is reduced to the same localized path, as an
 *   absolute Content URL is;
 * - an absolute URL on any other origin is an external link and stays as it is.
 *
 * Examples (locale = "fi-FI", page on https://wehunt.app):
 *   "/fi-fi/tarvikkeet?device=artemis"            → "/fi-fi/tarvikkeet?device=artemis"
 *   "https://wehunt.app/global/accessories#top"   → "/fi-fi/accessories#top"
 *   "https://support.example.com/connect"         → unchanged
 */
export function resolveCmsUrl(url: string, locale: string): string {
  const trimmed = url.trim();
  let target = trimmed;

  if (/^https?:\/\//i.test(trimmed)) {
    let parsed: URL;
    try {
      parsed = new URL(trimmed);
    } catch {
      return trimmed;
    }
    if (
      typeof window === "undefined" ||
      parsed.origin !== window.location.origin
    ) {
      return trimmed;
    }
    target = `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } else if (!trimmed.startsWith("/") || trimmed.startsWith("//")) {
    // Not a site path ("mailto:", "#", protocol-relative): nothing to localize.
    return trimmed;
  }

  const suffixStart = target.search(/[?#]/);
  const path = suffixStart < 0 ? target : target.slice(0, suffixStart);
  const suffix = suffixStart < 0 ? "" : target.slice(suffixStart);
  return `${localizeUrl(path || "/", locale)}${suffix}`;
}
